/**
 * Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { Container, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  // This lifecycle method catches errors in the child components
  // eslint-disable-next-line no-unused-vars
  static getDerivedStateFromError(error) {
    // Update state to show fallback UI
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      // Render a fallback UI if an error is caught
      return (
        <>
          <Container
            maxWidth="md"
            sx={ {
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight: "100vh"
            } }
          >
            <Container>
              <Typography variant="h4" component={ "div" } sx={ { my: 2 } }>
                Oops! Something went wrong.
              </Typography>
              <Typography variant="h6" component={ "div" } sx={ { fontWeight: "normal", my: 2 } }>
                We&apos;re sorry, but an unexpected error occurred.
              </Typography>
            </Container>
          </Container>
        </>
      );
    }

    // If no error, render children
    return this.props.children;
  }
}

// Validate 'children' prop using PropTypes
ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

export default ErrorBoundary;
